import allLocales from "@fullcalendar/core/locales-all";
import { Calendar } from "fullcalendar";

import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import type { IRoom } from "@/data/model/Room";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { TastierGlobal } from "@/portal/globalAccess";

export class OfferScheduleWindow extends BaseModal {
    private calendar: any;
    private $calendarContainer: JQuery;
    private dao: CatalogDao;
    private roomSelector?: Select<IRoom>;
    private $legend: JQuery;

    private needsRefetch = false;

    constructor() {
        super(i18n("modal.offerSchedule.title"), "modal-xl", true);
        this.$element.addClass("offerschedulepreview");

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        this.$calendarContainer = $("<div />").appendTo(this.$body);

        const calendarSlotLabelFormat = {
            hour: "2-digit",
            minute: "2-digit",
            omitZeroMinute: false,
            hour12: false, // Set to false for 24-hour format
        };

        this.calendar = new Calendar(this.$calendarContainer[0], {
            initialView: "timeGridWeek",
            selectable: false,
            aspectRatio: 2.5,
            //   timeZone: 'UTC',
            scrollTime: "09:00:00",
            editable: false,
            locales: allLocales,
            locale: TastierGlobal.getUserLocale(),
            firstDay: 1,
            views: {
                timeGridWeek: {
                    slotLabelFormat: calendarSlotLabelFormat as any,
                },
                timeGridDay: {
                    slotLabelFormat: calendarSlotLabelFormat as any,
                },
            },
            eventClick: (e) => {
                const url = e.event.url;
                if (url != null && url != "") {
                    window.open(e.event.url, "_blank");
                    e.jsEvent.preventDefault();
                }

                return false;
            },
            headerToolbar: {
                left: "",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay today prev,next",
            },
            // like '14:30'. A slot until 24:00 ends just before midnight, like a whole day, and shows 24:00
            eventTimeFormat: (arg) => {
                const time = (d) =>
                    d.hour === 23 && d.minute === 59 && d.second === 59
                        ? "24:00"
                        : Misc.padDigits(d.hour, 2) + ":" + Misc.padDigits(d.minute, 2);

                return arg.end ? time(arg.start) + arg.defaultSeparator + time(arg.end) : time(arg.start);
            },
            events: async (info, successCallback, failureCallback) => {
                try {
                    const currentSchedule = await this.dao.getOfferSchedulePreview({
                        start: DateTime.serializeIsoDate(info.start) as any,
                        end: DateTime.serializeIsoDate(info.end) as any,
                        roomId: this.roomSelector === undefined ? undefined : this.roomSelector.getValue(),
                    });

                    const availableColors = [
                        "#89b841",
                        "#5d78ff",
                        "#5e34be",
                        "#ff8a00",
                        "#ae4040",
                        "#484848",
                        "#ff69b4",
                        "#008080",
                        "#ffd700",
                        "#9932cc",
                    ];
                    const colorMap = [];
                    let colorIndex = 0;
                    this.$legend.empty();

                    //here
                    const rooms = new Array<{ roomId: string; roomName: string; roomOrder?: number }>();
                    const events: any[] = [];

                    // process "isForAllUseCases" events first
                    const forAllApplicationsSchedules = currentSchedule.filter((x) => x.isForAllUseCases);
                    const displayedForAllUseCasesEvents: any[] = [];

                    // setup room collection
                    const roomIds = new Set(currentSchedule.map((x) => x.roomId));
                    for (const roomId of roomIds) {
                        const itemWithRoom = currentSchedule.find((x) => x.roomId == roomId);
                        if (!itemWithRoom) continue;

                        rooms[roomId] = {
                            roomId: roomId,
                            roomName: itemWithRoom.roomName,
                            roomOrder: itemWithRoom.roomOrder,
                        };

                        colorMap[roomId] = availableColors[colorIndex];
                        if (colorIndex + 1 < availableColors.length) {
                            colorIndex++;
                        } else {
                            colorIndex = 0;
                        }
                    }

                    for (const offerSchedule of forAllApplicationsSchedules) {
                        // display only one event if there are multiple for same room and time
                        const eventAlreadyAdded = displayedForAllUseCasesEvents.some(
                            (x) =>
                                x.roomId == offerSchedule.roomId &&
                                x.startTime == offerSchedule.start.getTime() &&
                                x.endTime == offerSchedule.end.getTime()
                        );

                        if (eventAlreadyAdded) {
                            continue;
                        }

                        displayedForAllUseCasesEvents.push({
                            roomId: offerSchedule.roomId,
                            startTime: offerSchedule.start.getTime(),
                            endTime: offerSchedule.end.getTime(),
                        });
                        //end display only one event

                        events.push({
                            title: offerSchedule.isClosedTime
                                ? i18n("enums.OfferScheduleType.Closed")
                                : offerSchedule.offerName,
                            start: DateTime.convertToUTCOld(offerSchedule.start),
                            end: DateTime.convertToUTCOld(offerSchedule.end),
                            description: offerSchedule.roomName,
                            borderColor: this.shadeColor(colorMap[offerSchedule.roomId], -20),
                            backgroundColor: colorMap[offerSchedule.roomId],
                            textColor: "#ffffff",
                            url: offerSchedule.isClosedTime ? "" : "/offer/" + offerSchedule.offerId,
                            id: offerSchedule.id,
                            allDay: offerSchedule.isAllDay,
                        });
                    }

                    // process NOT "isForAllUseCases" events
                    const notForAllApplicationsSchedules = currentSchedule.filter((x) => x.isForAllUseCases == false);
                    const displayedNotForAllUseCasesEvents: any[] = [];

                    for (const offerSchedule of notForAllApplicationsSchedules) {
                        // display only one event if there are multiple for same room and time
                        const eventAlreadyAdded = displayedNotForAllUseCasesEvents.some(
                            (x) =>
                                x.roomId == offerSchedule.roomId &&
                                x.startTime == offerSchedule.start.getTime() && x.endTime == offerSchedule.end.getTime()
                        );
                        if (eventAlreadyAdded) {
                            continue;
                        }

                        displayedNotForAllUseCasesEvents.push({
                            roomId: offerSchedule.roomId,
                            startTime: offerSchedule.start.getTime(),
                            endTime: offerSchedule.end.getTime(),
                        });
                        //end display only one event

                        events.push({
                            title: offerSchedule.isClosedTime
                                ? i18n("enums.OfferScheduleType.Closed") +
                                  " - " +
                                  i18n("modal.offerSchedule.differentOffers")
                                : i18n("modal.offerSchedule.differentOffers"),
                            start: DateTime.convertToUTCOld(offerSchedule.start),
                            end: DateTime.convertToUTCOld(offerSchedule.end),
                            description: offerSchedule.roomName,
                            borderColor: this.shadeColor(colorMap[offerSchedule.roomId], -20),
                            backgroundColor: colorMap[offerSchedule.roomId],
                            textColor: "#ffffff",
                            url: "",
                            id: offerSchedule.id,
                            allDay: offerSchedule.isAllDay,
                        });
                    }

                    const sortedRooms = Object.keys(rooms)
                        .map((e) => {
                            return rooms[e];
                        })
                        .sort((a, b) => {
                            const sortA = a.roomOrder ?? Number.MAX_SAFE_INTEGER;
                            const sortB = b.roomOrder ?? Number.MAX_SAFE_INTEGER;

                            if (sortA === sortB) return a.roomName.localeCompare(b.roomName);

                            return sortA - sortB;
                        });

                    for (const room of sortedRooms) {
                        this.$legend.append(
                            '<div class="room"><div class="color" style="background-color:' +
                                colorMap[room.roomId] +
                                '"></div>' +
                                Format.htmlEscape(room.roomName) +
                                "</div>"
                        );
                    }

                    successCallback(events);
                } catch (ex) {
                    logError(ex);
                    failureCallback(ex as Error);
                }
            },
        });

        window.setTimeout(() => {
            this.calendar.render();
            this.addRoomFilter();
        }, 200);

        this.$legend = $('<div class="legend" />').appendTo(this.$body);
    }

    private onRoomChanged() {
        this.calendar.refetchEvents();
    }

    public onShow() {
        if (this.needsRefetch) {
            this.calendar.refetchEvents();
            this.needsRefetch = false;
        }

        if (!this.calendar.isRendered || this.roomSelector === undefined) {
            this.calendar.render();
            this.addRoomFilter();
        }
    }

    public onHide() {
        this.calendar.removeAllEvents();
        this.calendar.destroy();
        this.roomSelector?.destroy();
        this.roomSelector = undefined;
        this.needsRefetch = true;
    }

    private addRoomFilter() {
        if (this.roomSelector !== undefined) this.roomSelector.destroy();

        const toolBar = this.$calendarContainer.find(".fc-header-toolbar > .fc-toolbar-chunk:first");
        this.roomSelector = new Select<IRoom>(
            undefined,
            Factory.getInstance(RoomDao),
            (c) => c.name,
            false,
            i18n("offerSchedule.allRooms")
        ).renderTo(toolBar);
        this.roomSelector.onChange = () => this.onRoomChanged();
    }

    private shadeColor(color: string, percent: number): string {
        let R = parseInt(color.substring(1, 3), 16);
        let G = parseInt(color.substring(3, 5), 16);
        let B = parseInt(color.substring(5, 7), 16);

        R = parseInt(((R * (100 + percent)) / 100) as any);
        G = parseInt(((G * (100 + percent)) / 100) as any);
        B = parseInt(((B * (100 + percent)) / 100) as any);

        R = R < 255 ? R : 255;
        G = G < 255 ? G : 255;
        B = B < 255 ? B : 255;

        const RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
        const GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
        const BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

        return "#" + RR + GG + BB;
    }
}
